import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingCart, Download, CheckCircle, Clock, XCircle, MessageCircle } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SupportTicketModal } from './SupportTicketModal';
import { OrderDetailsModal } from './OrderDetailsModal';
import { wsService } from '../services/WebSocketService';

interface Order {
  orderId: string;
  county: string;
  state: string;
  amount: number;
  stripePaymentId: string;
  status: 'paid' | 'pending' | 'failed';
  fulfilled: boolean;
  createdAt: string;
  downloadUrl?: string;
  supportTickets?: number;
}

interface MyOrdersProps {
  highlightOrderId?: string;
}

export function MyOrders({ highlightOrderId }: MyOrdersProps = {}) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [highlightedOrder, setHighlightedOrder] = useState<string | null>(null);

  useEffect(() => {
    fetchMyOrders();

    // Listen for WebSocket updates to orders
    const handleOrderUpdate = (message: any) => {
      if (message.type === 'order-created' || message.type === 'order-updated') {
        // Refresh orders list when we get an update
        fetchMyOrders();
      }
    };

    wsService.on('orderUpdate', handleOrderUpdate);

    return () => {
      wsService.off('orderUpdate', handleOrderUpdate);
    };
  }, []);

  // Scroll to and highlight order when highlightOrderId changes
  useEffect(() => {
    if (highlightOrderId && orders.length > 0) {
      setHighlightedOrder(highlightOrderId);

      // Find the order and auto-open chat
      const order = orders.find(o => o.orderId === highlightOrderId);
      if (order) {
        setSelectedOrder(order);
        setShowSupportModal(true);
      }

      // Scroll to the order after a brief delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.getElementById(`order-${highlightOrderId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);

      // Remove highlight after 3 seconds
      setTimeout(() => {
        setHighlightedOrder(null);
      }, 3000);
    }
  }, [highlightOrderId, orders]);

  const fetchMyOrders = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token found');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/taxlatesdata-orders/my-orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();
      setOrders(data.orders || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
      // Show empty list on error - no fake data
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      paid: 'bg-green-100 text-green-800 border-green-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
    };

    const icons = {
      paid: <CheckCircle className="w-4 h-4" />,
      pending: <Clock className="w-4 h-4" />,
      failed: <XCircle className="w-4 h-4" />,
    };

    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold border ${styles[status as keyof typeof styles]}`}>
        {icons[status as keyof typeof icons]}
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading your orders...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-tdb-accent rounded-xl flex items-center justify-center">
            <ShoppingCart className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-tdb-primary">My Orders</h2>
            <p className="text-sm text-gray-600">View your tax delinquent list purchases</p>
          </div>
        </div>
      </div>

      {/* Orders List */}
      {orders.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Orders Yet</h3>
          <p className="text-gray-600">You haven't purchased any tax delinquent lists yet.</p>
          <p className="text-sm text-gray-500 mt-1">Purchase a list above to get started!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {orders.map((order) => (
            <div
              key={order.orderId}
              id={`order-${order.orderId}`}
              onClick={() => {
                setSelectedOrder(order);
                setShowOrderDetails(true);
              }}
              className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 cursor-pointer hover:shadow-xl hover:scale-[1.01] ${
                highlightedOrder === order.orderId
                  ? 'ring-4 ring-purple-500 ring-opacity-50 scale-[1.02]'
                  : ''
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {order.county}, {order.state}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Order #{order.orderId.slice(0, 12)}... • {new Date(order.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(order.status)}
                    {order.fulfilled ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Fulfilled
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                        <Clock className="w-4 h-4 mr-1" />
                        Processing
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-xs text-gray-600 mb-1">Amount Paid</div>
                    <div className="text-xl font-bold text-gray-900">${order.amount}</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-xs text-gray-600 mb-1">Payment ID</div>
                    <div className="text-sm font-mono text-gray-900">{order.stripePaymentId.slice(0, 20)}...</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-xs text-gray-600 mb-1">Status</div>
                    <div className="text-sm font-semibold text-gray-900">{order.fulfilled ? 'Delivered' : 'Processing'}</div>
                  </div>
                </div>

                <div className="flex items-center justify-end pt-4 border-t border-gray-200">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedOrder(order);
                      setShowSupportModal(true);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-tdb-accent hover:bg-tdb-accent-dark text-white rounded-xl transition-colors font-semibold"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>Talk to the Team</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal
          isOpen={showOrderDetails}
          onClose={() => {
            setShowOrderDetails(false);
            setSelectedOrder(null);
          }}
          order={selectedOrder}
          onOpenChat={() => {
            setShowOrderDetails(false);
            setShowSupportModal(true);
          }}
          onRefresh={fetchMyOrders}
          isAdmin={false}
        />
      )}

      {/* Support Ticket Modal */}
      {selectedOrder && (
        <SupportTicketModal
          isOpen={showSupportModal}
          onClose={() => {
            setShowSupportModal(false);
            setSelectedOrder(null);
          }}
          orderId={selectedOrder.orderId}
          isAdmin={false}
        />
      )}
    </div>
  );
}
